import {
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Download, Pencil, Plus, Wrench } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../../components/shared/PageHeader';
import { isOpenAiRepairConfigured } from '../../../features/research/ai-repair';
import { repairSessionsInParallel } from '../../../features/research/packages/session-repair';
import { WorkspaceExportModal } from '../../../features/research/packages/spectral-workspace/components/WorkspaceExportModal';
import { invalidateWorkspaceSessionQueries } from '../../../features/research/packages/spectral-workspace/queries/invalidateWorkspaceSessionQueries';
import {
  ReturnToAnalysisBanner,
  SessionGroupResearchUsagesCard,
  SessionGroupSessionsTable,
  SessionPickerModal,
  buildGroupSessionHref,
  invalidateSessionGroupQueries,
  sessionGroupStore,
  useSessionGroupSessions,
  useSessionGroupWorkspaceExport,
} from '../../../features/session-groups';
import { invalidateSessionRepairCaches } from '../../../features/session-groups/utils/invalidateSessionRepairCaches';
import { useAuthUser } from '../../../providers/AuthProvider';

export function SessionGroupDetailPage() {
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const fromAnalysis = searchParams.get('fromAnalysis');
  const queryClient = useQueryClient();
  const user = useAuthUser();
  const {
    group,
    tableRows,
    needsRepairSessionIds,
    reloadSessions,
    isLoading,
    error,
  } = useSessionGroupSessions(groupId, user?.id?.toString());

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [removingSessionId, setRemovingSessionId] = useState<number | null>(
    null
  );
  const [isRepairingAll, setIsRepairingAll] = useState(false);
  const [repairProgress, setRepairProgress] = useState({ done: 0, total: 0 });
  const [pickerOpened, { open: openPicker, close: closePicker }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);

  const groupExport = useSessionGroupWorkspaceExport({
    group,
    needsRepairSessionIds,
  });

  const existingSessionIds = group?.sessionIds ?? [];

  const persistGroup = useCallback(
    async (
      sessionIds: number[],
      successMessage: string,
      meta?: { name: string; description?: string }
    ) => {
      if (!groupId || !group) return false;

      const trimmedName = (meta?.name ?? group.name).trim();
      const descriptionValue = meta?.description ?? group.description ?? '';

      if (!trimmedName) {
        notifications.show({
          color: 'red',
          title: 'Nome richiesto',
          message: 'Inserisci un nome per il gruppo.',
        });
        return false;
      }

      setIsSavingMeta(true);
      try {
        await sessionGroupStore.updateGroup(groupId, {
          name: trimmedName,
          description: descriptionValue.trim() || undefined,
          sessionIds,
        });
        invalidateSessionGroupQueries(queryClient, groupId);
        notifications.show({
          color: 'teal',
          title: 'Gruppo aggiornato',
          message: successMessage,
        });
        return true;
      } catch (updateError) {
        notifications.show({
          color: 'red',
          title: 'Salvataggio non riuscito',
          message:
            updateError instanceof Error
              ? updateError.message
              : 'Impossibile aggiornare il gruppo.',
        });
        return false;
      } finally {
        setIsSavingMeta(false);
      }
    },
    [group, groupId, queryClient]
  );

  const handleOpenEditModal = () => {
    if (!group) return;
    setEditName(group.name);
    setEditDescription(group.description ?? '');
    openEditModal();
  };

  const handleCloseEditModal = () => {
    closeEditModal();
  };

  const handleSaveMeta = async () => {
    if (!group) return;

    const saved = await persistGroup(
      group.sessionIds,
      'Le modifiche al gruppo sono state salvate.',
      { name: editName, description: editDescription }
    );

    if (saved) {
      handleCloseEditModal();
    }
  };

  const handleAddSessions = async (
    entries: Array<{
      sessionId: number;
      patientCode?: string | null;
    }>
  ) => {
    if (!group) return;

    const known = new Set(group.sessionIds);
    const additions = entries
      .map(entry => entry.sessionId)
      .filter(sessionId => !known.has(sessionId));

    if (additions.length === 0) {
      return;
    }

    await persistGroup(
      [...group.sessionIds, ...additions],
      `${additions.length} sessioni aggiunte al gruppo.`
    );
    closePicker();
  };

  const handleRemoveSession = async (sessionId: number) => {
    if (!group) return;

    setRemovingSessionId(sessionId);
    const nextSessionIds = group.sessionIds.filter(id => id !== sessionId);
    const saved = await persistGroup(
      nextSessionIds,
      'La sessione è stata rimossa dal gruppo.'
    );
    if (saved) {
      invalidateWorkspaceSessionQueries(queryClient, [sessionId]);
    }
    setRemovingSessionId(null);
  };

  const getOpenHref = useCallback(
    (sessionId: number) => {
      if (!groupId) {
        return '/sessions/group';
      }

      return buildGroupSessionHref(
        groupId,
        sessionId,
        fromAnalysis ?? undefined
      );
    },
    [fromAnalysis, groupId]
  );

  const handleRepairAll = async () => {
    if (needsRepairSessionIds.length === 0) {
      return;
    }

    if (!isOpenAiRepairConfigured()) {
      notifications.show({
        color: 'yellow',
        title: 'Riparazione non disponibile',
        message:
          'OpenAI non configurato. Imposta VITE_OPENAI_API_KEY in .env.local e riavvia il dev server.',
      });
      return;
    }

    setIsRepairingAll(true);
    setRepairProgress({ done: 0, total: needsRepairSessionIds.length });

    try {
      const summary = await repairSessionsInParallel(needsRepairSessionIds, {
        onProgress: (completed, total) => {
          setRepairProgress({ done: completed, total });
        },
      });

      await invalidateSessionRepairCaches(queryClient, {
        userId: user?.id?.toString(),
        sessionIds: needsRepairSessionIds,
        groupId,
      });
      reloadSessions();

      const color =
        summary.failedCount > 0
          ? summary.successCount > 0
            ? 'yellow'
            : 'red'
          : 'teal';

      notifications.show({
        color,
        title: 'Riparazione gruppo completata',
        message: `${summary.successCount} riparate, ${summary.failedCount} fallite, ${summary.noOpCount} senza intervento.`,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Riparazione gruppo fallita',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile completare la riparazione di gruppo.',
      });
    } finally {
      setIsRepairingAll(false);
      setRepairProgress({ done: 0, total: 0 });
    }
  };

  if (!groupId) {
    return <Text c='red'>Gruppo non valido.</Text>;
  }

  return (
    <>
      <PageHeader
        title={group?.name ?? 'Gruppo sessioni'}
        subtitle={group?.description?.trim() || 'Nessuna descrizione'}
        breadcrumbs={[
          { label: 'Sessioni', href: '/sessions' },
          { label: 'Gruppi sessioni', href: '/sessions/group' },
          { label: group?.name ?? groupId },
        ]}
        backUrl='/sessions/group'
        actions={
          group ? (
            <Group gap='sm'>
              <Button
                variant='light'
                leftSection={<Download size={16} />}
                onClick={groupExport.open}
                disabled={!groupExport.canExport}
                title={
                  !groupExport.canExport
                    ? 'Aggiungi almeno una sessione al gruppo'
                    : undefined
                }
              >
                Esporta Excel
              </Button>
              <Button
                variant='light'
                leftSection={<Pencil size={16} />}
                onClick={handleOpenEditModal}
              >
                Modifica dettagli
              </Button>
            </Group>
          ) : undefined
        }
      />

      <Divider my='md' />

      <Stack gap='md'>
        {fromAnalysis && <ReturnToAnalysisBanner analysisId={fromAnalysis} />}

        {isLoading ? (
          <Stack align='center' py='xl'>
            <Loader />
            <Text size='sm' c='dimmed'>
              Caricamento gruppo…
            </Text>
          </Stack>
        ) : error ? (
          <Text c='red'>
            {error instanceof Error
              ? error.message
              : 'Impossibile caricare il gruppo.'}
          </Text>
        ) : !group ? (
          <Text c='red'>Gruppo non trovato.</Text>
        ) : (
          <>
            <SessionGroupResearchUsagesCard groupId={groupId} />

            <Card withBorder padding={0}>
              <Stack gap={0}>
                <Group
                  justify='space-between'
                  align='flex-end'
                  p='md'
                  wrap='wrap'
                >
                  <Stack gap={4}>
                    <Title order={5}>Sessioni nel gruppo</Title>
                    <Text size='sm' c='dimmed'>
                      {tableRows.length} sessioni
                    </Text>
                  </Stack>
                  <Group gap='xs'>
                    {needsRepairSessionIds.length > 0 && (
                      <Button
                        variant='light'
                        color='orange'
                        leftSection={<Wrench size={16} />}
                        loading={isRepairingAll}
                        disabled={!isOpenAiRepairConfigured()}
                        title={
                          !isOpenAiRepairConfigured()
                            ? 'OpenAI non configurato. Imposta VITE_OPENAI_API_KEY in .env.local per abilitare la riparazione.'
                            : undefined
                        }
                        onClick={() => void handleRepairAll()}
                      >
                        {isRepairingAll
                          ? `Riparazione in corso (${repairProgress.done}/${repairProgress.total})…`
                          : `Ripara tutto (${needsRepairSessionIds.length})`}
                      </Button>
                    )}
                    <Button
                      leftSection={<Plus size={16} />}
                      onClick={openPicker}
                    >
                      Aggiungi sessioni
                    </Button>
                  </Group>
                </Group>

                <Divider />

                <SessionGroupSessionsTable
                  rows={tableRows}
                  getOpenHref={getOpenHref}
                  onRemove={sessionId => void handleRemoveSession(sessionId)}
                  removingSessionId={removingSessionId}
                />
              </Stack>
            </Card>
          </>
        )}
      </Stack>

      <SessionPickerModal
        opened={pickerOpened}
        onClose={closePicker}
        userId={user?.id?.toString()}
        existingSessionIds={existingSessionIds}
        onAddSessions={entries => void handleAddSessions(entries)}
      />

      <WorkspaceExportModal
        opened={groupExport.opened}
        onClose={groupExport.close}
        exportingId={groupExport.exportingId}
        isPreparingExportData={groupExport.isPreparingExportData}
        onExport={exportId => void groupExport.handleExportSingle(exportId)}
        canExport={groupExport.canExport}
        excelExports={groupExport.excelExports}
      />

      <Modal
        opened={editModalOpened}
        onClose={handleCloseEditModal}
        title='Modifica dettagli gruppo'
        centered
      >
        <Stack gap='md'>
          <TextInput
            label='Nome'
            value={editName}
            onChange={event => setEditName(event.currentTarget.value)}
          />
          <Textarea
            label='Descrizione'
            value={editDescription}
            onChange={event => setEditDescription(event.currentTarget.value)}
            minRows={3}
          />
          <Group justify='flex-end'>
            <Button variant='subtle' onClick={handleCloseEditModal}>
              Annulla
            </Button>
            <Button
              loading={isSavingMeta}
              onClick={() => void handleSaveMeta()}
            >
              Salva
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
